import {
  FieldArray,
  Form,
  Formik,
  useFormikContext,
  type FormikProps,
} from "formik";
import { useState } from "react";
import {
  IconBuilding,
  IconUser,
  type IconProps,
  IconPlus,
} from "@tabler/icons-react";
import { produce } from "immer";
import type { ComponentType } from "react";
import Button from "../UI/Button";
import ButtonCard from "../UI/ButtonCard";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
// @ts-expect-error pure js
import ProfileFormPerso from "./ProfileFormPerso";
// @ts-expect-error pure js
import ProfileFormDom from "./ProfileFormDom";
import {
  PROFILE_BAILLEUR_INITIAL_VALUE,
  PROFILE_INITIAL_VALUE,
  profileBailleurSchema,
  profileMandataireSchema,
  StatusFiscal,
  StatusFiscalDescription,
  // @ts-expect-error pure js
} from "../../models/profile";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import MandataireFormFields from "../Account/MandataireFormFields";
// @ts-expect-error pure js
import SiretFields from "../UI/SiretFields";
// @ts-expect-error pure js
import IconAndText from "../UI/IconAndText";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select";
// @ts-expect-error pure js
import useAccount from "../../hooks/use-account";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import type { ProfileBailleur } from "./types";
import styles from "./ProfileBailleurCreateWizard.module.css";

const PROFILE_TYPE_NOM_PROPRE = "nom_propre";
const PROFILE_TYPE_SOCIETE = "societe";

type ProfileType = typeof PROFILE_TYPE_NOM_PROPRE | typeof PROFILE_TYPE_SOCIETE;

interface ProfileTypeChoice {
  type: ProfileType;
  label: string;
  icon: ComponentType<IconProps>;
}

/** Les champs du formulaire réellement lus ici ; le reste vient des valeurs initiales. */
interface WizardProfileValues {
  type: string;
  nom_societe?: string;
  siret?: string;
}

interface WizardFormValues {
  nom_profile?: string;
  siret?: string;
  is_societe: boolean;
  profiles: WizardProfileValues[];
}

interface ProfileBailleurCreateWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (created?: ProfileBailleur) => void;
}

const PROFILE_TYPE_CHOICES: ProfileTypeChoice[] = [
  {
    type: PROFILE_TYPE_NOM_PROPRE,
    label: "Nom propre",
    icon: IconUser,
  },
  {
    type: PROFILE_TYPE_SOCIETE,
    label: "Société (SCI IR, SCI IS, SAS, SARL, ...)",
    icon: IconBuilding,
  },
];

const OPTIONS_NOM_PROPRE = [
  StatusFiscal.NUMICRO,
  StatusFiscal.NUREEL,
  StatusFiscal.LMNPMICRO,
  StatusFiscal.LMNPREEL,
].map((status: string) => ({
  label: StatusFiscalDescription[status],
  value: status,
}));

function StepTypeSelection({
  onSelect,
}: {
  onSelect: (type: ProfileType) => void;
}) {
  return (
    <>
      <Text>Choisissez le type de profil bailleur à créer</Text>
      {PROFILE_TYPE_CHOICES.map((choice) => (
        <ButtonCard key={choice.type} onClick={() => onSelect(choice.type)}>
          <IconAndText icon={<choice.icon size={24} />} text={choice.label} />
        </ButtonCard>
      ))}
    </>
  );
}

function StepSiret() {
  return (
    <SiretFields
      nameSiret="siret"
      nameNomSociete="profiles[0].nom_societe"
      nameNom="profiles[0].nom"
      namePrenom="profiles[0].prenom"
      nameAdresse="profiles[0].adresse"
      nameVille="profiles[0].ville"
      nameCp="profiles[0].cp"
      nameDebutActivite="debut_activite"
      nameNumeroTva="numero_tva"
      nameSocType="profiles[0].socType"
      label="SIRET / Nom de l'entreprise"
      placeholder="12345678901234 ou Nom de l'entreprise"
      mandatory
    />
  );
}

interface StepPersonalInfoProps {
  isNomPropre: boolean;
  isPro: boolean;
  error: string;
}

function StepPersonalInfo({
  isNomPropre,
  isPro,
  error,
}: StepPersonalInfoProps) {
  const { values } = useFormikContext<WizardFormValues>();
  return (
    <>
      <TextInput label="Nom du profil" name="nom_profile" marginForm="" />
      {isNomPropre && (
        <>
          <ReactSelect
            name="regime_fiscal"
            label="Régime fiscal"
            options={OPTIONS_NOM_PROPRE}
          />
          <Callout variant="info">
            Vous ne savez pas quoi choisir ? Pas d'inquiétude ! <br />
            Indiquez « {StatusFiscalDescription[StatusFiscal.LMNPMICRO]} » si le
            logement est loué meublé, «{" "}
            {StatusFiscalDescription[StatusFiscal.NUMICRO]} » s'il est loué non
            meublé. Vous pourrez le modifier à tout moment depuis les paramètres
            du profil.
          </Callout>
        </>
      )}
      {isPro && <MandataireFormFields />}
      <FieldArray
        name="profiles"
        render={(arrayHelpers) => (
          <div className={styles.profiles}>
            {values.profiles.map((profile, index) => (
              <Card padding="md" key={index}>
                <ProfileFormPerso
                  prefix={`profiles[${index}].`}
                  type={profile.type}
                  isBailleur
                  hideTypeSelection
                />
                <ProfileFormDom isBailleur prefix={`profiles[${index}].`} />
                {index !== 0 && (
                  <div className={styles.removeProfile}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => arrayHelpers.remove(index)}
                    >
                      Supprimer le profil
                    </Button>
                  </div>
                )}
              </Card>
            ))}
            {isNomPropre && (
              <Button
                variant="secondary"
                type="button"
                icon={IconPlus}
                onClick={() => arrayHelpers.push(PROFILE_INITIAL_VALUE)}
              >
                Ajouter un copropriétaire
              </Button>
            )}
          </div>
        )}
      />
      <Callout variant="danger">{error}</Callout>
    </>
  );
}

export default function ProfileBailleurCreateWizard({
  open,
  onOpenChange,
  onSubmit,
}: ProfileBailleurCreateWizardProps) {
  const { addProfileBailleur } = useProfileBailleurs();
  const [error, setError] = useState("");
  const { account } = useAccount();
  const [step, setStep] = useState(1);
  const [profileType, setProfileType] = useState<ProfileType | null>(null);

  if (!account) return null;

  const isSociete = profileType === PROFILE_TYPE_SOCIETE;
  const isNomPropre = profileType === PROFILE_TYPE_NOM_PROPRE;
  const isPro = account.home === "/pro";

  function selectType(type: ProfileType) {
    setProfileType(type);
    setStep(type === PROFILE_TYPE_SOCIETE ? 2 : 3);
  }

  function backToTypeSelection(resetForm: () => void) {
    setStep(1);
    setProfileType(null);
    resetForm();
  }

  function goToPersonalInfo(
    values: WizardFormValues,
    setFieldValue: FormikProps<WizardFormValues>["setFieldValue"],
  ) {
    if (!values.nom_profile && values.profiles[0]?.nom_societe) {
      setFieldValue("nom_profile", values.profiles[0].nom_societe);
    }
    setStep(3);
  }

  function buildInitialValues(): WizardFormValues {
    return {
      ...PROFILE_BAILLEUR_INITIAL_VALUE,
      is_societe: isSociete,
      profiles: [{ ...PROFILE_INITIAL_VALUE, type: isSociete ? "2" : "1" }],
    };
  }

  const validationSchema = isPro
    ? profileMandataireSchema
    : profileBailleurSchema;

  async function submitHandler(values: WizardFormValues) {
    const sanitizedValues = sanitizeValues(values) as WizardFormValues;
    const profile = produce(sanitizedValues, (draft) => {
      draft.profiles.forEach((draftProfile) => {
        draftProfile.siret = sanitizedValues.siret;
      });
    });
    try {
      const created = await addProfileBailleur(profile);
      setError("");
      onSubmit(created);
    } catch (error) {
      setError(handleAPIError(error));
    }
  }

  return (
    <Formik
      key={profileType ?? "type-selection"}
      initialValues={buildInitialValues()}
      onSubmit={submitHandler}
      validationSchema={validationSchema}
    >
      {({ resetForm, values, setFieldValue }) => (
        <ModalDialog
          size="md"
          open={open}
          header="Créer un profil bailleur"
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              backToTypeSelection(resetForm);
              setError("");
            }
            onOpenChange(nextOpen);
          }}
        >
          <Form>
            <ModalDialogBody className={styles.body}>
              <Callout variant="success">
                Il s'agit de l'identité du bailleur. Ces informations sont
                nécessaires car indiquées dans le bail, quittance, et tout
                document à destination du locataire.
              </Callout>

              {step === 1 && <StepTypeSelection onSelect={selectType} />}
              {step === 2 && <StepSiret />}
              {step === 3 && (
                <StepPersonalInfo
                  isNomPropre={isNomPropre}
                  isPro={isPro}
                  error={error}
                />
              )}
            </ModalDialogBody>

            {step === 2 && (
              <ModalDialogFooter
                start={
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => backToTypeSelection(resetForm)}
                  >
                    Précédent
                  </Button>
                }
              >
                <Button
                  type="button"
                  onClick={() => goToPersonalInfo(values, setFieldValue)}
                >
                  Suivant
                </Button>
              </ModalDialogFooter>
            )}
            {step === 3 && (
              <ModalDialogFooter
                start={
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      isSociete ? setStep(2) : backToTypeSelection(resetForm)
                    }
                  >
                    Précédent
                  </Button>
                }
              >
                <FormikSubmitButton>Valider</FormikSubmitButton>
              </ModalDialogFooter>
            )}
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
